// Periodos de un plan de plataforma. El backend devuelve una fila por periodo
// de cada plan (mismo `code`, distinto `period`); aquí se agrupan por `code`
// para mostrar un plan con su selector de periodo. `PLAN_PERIOD_MONTHS` está
// duplicado a propósito de backend (`platform-plan.model.ts`): el backend es
// la fuente de verdad.

export const PLAN_PERIODS = ["monthly", "quarterly", "yearly"];

export const PLAN_PERIOD_LABELS = {
    monthly: "Mensual",
    quarterly: "Trimestral",
    yearly: "Anual"
};

// Para "/mes" junto al precio.
export const PLAN_PERIOD_SUFFIXES = {
    monthly: "/mes",
    quarterly: "/trimestre",
    yearly: "/año"
};

// Para frases: "el primer mes", "al trimestre".
export const PLAN_PERIOD_UNITS = {
    monthly: "mes",
    quarterly: "trimestre",
    yearly: "año"
};

export const PLAN_PERIOD_MONTHS = {
    monthly: 1,
    quarterly: 3,
    yearly: 12
};

export function isPlanPeriod(period) {
    return PLAN_PERIODS.includes(period);
}

// Agrupa las filas por `code`. Cada grupo: { code, name, description, rows }
// con `rows` indexado por periodo. Los grupos van de menor a mayor precio de
// su fila mensual (o de la primera fila que tengan).
export function groupPlansByCode(plans) {
    const groups = [];
    (plans || []).forEach((plan) => {
        let group = groups.find((item) => item.code === plan.code);
        if (!group) {
            group = {
                code: plan.code,
                name: plan.name,
                description: plan.description,
                rows: {}
            };
            groups.push(group);
        }
        group.rows[plan.period] = plan;
    });
    const basePrice = (group) => {
        const row = group.rows.monthly || Object.values(group.rows)[0];
        return row.price;
    };
    return groups.sort((a, b) => basePrice(a) - basePrice(b));
}

// Periodos que existen en al menos un grupo, en el orden de PLAN_PERIODS.
export function availablePeriods(groups) {
    return PLAN_PERIODS.filter((period) =>
        (groups || []).some((group) => group.rows[period])
    );
}

// Fila del grupo para el periodo pedido; si no existe, la mensual y, como
// último recurso, la primera que tenga.
export function rowForPeriod(group, period) {
    return (
        group.rows[period] ||
        group.rows.monthly ||
        Object.values(group.rows)[0] ||
        null
    );
}

export function monthlyEquivalent(plan) {
    return Math.round(plan.price / PLAN_PERIOD_MONTHS[plan.period]);
}

// % de ahorro frente a la fila mensual del mismo plan, entero; 0 si no hay.
export function savingsPercent(plan, monthlyPlan) {
    if (!monthlyPlan || !monthlyPlan.price || plan.period === "monthly") {
        return 0;
    }
    const saving =
        1 - plan.price / (monthlyPlan.price * PLAN_PERIOD_MONTHS[plan.period]);
    return saving > 0 ? Math.round(saving * 100) : 0;
}

// Lo que se ahorra en dinero frente a pagar la fila mensual el mismo número
// de meses; 0 si no hay ahorro.
export function savingsAmount(plan, monthlyPlan) {
    if (!monthlyPlan || !monthlyPlan.price || plan.period === "monthly") {
        return 0;
    }
    const saving =
        monthlyPlan.price * PLAN_PERIOD_MONTHS[plan.period] - plan.price;
    return saving > 0 ? saving : 0;
}

const DAY_MS = 24 * 60 * 60 * 1000;

// Días que le quedan a un periodo (ej. la prueba) que termina en `endsAt`;
// 0 si ya terminó o no hay fecha.
export function daysUntil(endsAt) {
    if (!endsAt) return 0;
    const remainingMs = new Date(endsAt).getTime() - Date.now();
    return Math.max(0, Math.ceil(remainingMs / DAY_MS));
}
